/* ============================================================
   ResearchTab — marketing research inside the Plan section.
   States: empty → progress (live run) → report (teaser | full).
   Ported from research-pack/research-02/03/04/08/13; the paywall is
   server-side (locked sections arrive as {locked:true} shells), so this
   component never holds premium content for free users.
   ============================================================ */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/Icons.jsx';
import { api } from '../../services/api/client.js';
import { brandKitEnabled } from '../../lib/flags.js';
import { useUpgrade } from '../../contexts/UpgradeContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import CreationAccessNotice, { creationAccessState } from '../../components/billing/CreationAccessNotice.jsx';
import NoBrandGuard from '../../components/NoBrandGuard.jsx';
import { researchStartGate } from '../../lib/researchGate.js';
import { CompetitorCards, ArchetypeCards, ChannelBars, AnglePillars, Roadmap, EconomicsTable, PositioningBlock } from './ResearchBlocks.jsx';

const ACTIVE_STATUSES = ['queued', 'gathering', 'analyzing', 'synthesizing', 'verifying'];

/* Brand logo tile for the research headers — the real primary logo on a
   checkered tile, or the brand initial as a graceful fallback. */
function BrandTile({ logoUrl, name, size = 'lg' }) {
  const initial = ((name || 'D').trim()[0] || 'D').toUpperCase();
  return (
    <div className={`cpr-bid-logo cpr-bid-${size}`}>
      {logoUrl ? <img src={logoUrl} alt="" /> : <span className="cpr-bid-ini">{initial}</span>}
    </div>
  );
}
const POLL_MS = 5000;

// eta: static per-stage estimate shown on the right. count: a real number
// pulled from the report so the "done" line reads "38 sources gathered" etc.;
// when 0/undefined we fall back to the generic done label.
const STAGES = [
  { id: 'collect', key: 'stCollect', eta: '~60s', match: (r) => r.step === 'gather', count: (rep) => (rep.sources || []).length },
  { id: 'audience', key: 'stAudience', eta: '~40s', match: (r) => r.step === 'analyze:audience' || r.step === 'analyze:methodology', count: (rep) => rep.blocks?.archetypes?.length || rep.metrics?.audience?.segments?.length || 0 },
  { id: 'competitors', key: 'stCompetitors', eta: '~45s', match: (r) => r.step.startsWith('analyze:') && r.step !== 'analyze:audience' && r.step !== 'analyze:methodology', count: (rep) => rep.blocks?.competitors?.length || 0 },
  { id: 'synthesize', key: 'stSynthesize', eta: '~50s', match: (r) => r.step === 'synthesize', count: (rep) => (rep.sections || []).filter((s) => !s.locked && s.bodyMd).length || (rep.sections || []).length },
  { id: 'verify', key: 'stVerify', eta: '~20s', match: (r) => r.step === 'verify' },
];

const STAGE_ICON = { collect: 'search', audience: 'target', competitors: 'grid', synthesize: 'sparkles', verify: 'check' };

// Section kinds in the canonical deliverable order (lock-card teasers keyed by kind).
const KIND_ORDER = ['methodology', 'market', 'audience', 'competitors', 'positioning', 'economics', 'channels', 'content_angles', 'action_plan', 'risks'];

// Locked-section artefact type → which FABRICATED skeleton silhouette to blur.
// SECURE: these skeletons encode NO real data — for a free user the server sends
// no content for these sections at all; the blur is decorative, never a CSS blur
// over real data.
const LOCK_ARTEFACT = {
  market: 'chart', channels: 'chart', competitors: 'cards', content_angles: 'cards',
  economics: 'table', positioning: 'prose', action_plan: 'prose', risks: 'prose', methodology: 'prose',
};

function LockSkeleton({ kind }) {
  const type = LOCK_ARTEFACT[kind] || 'prose';
  if (type === 'chart') {
    return (
      <div className="cpr-skel cpr-skel-peek" aria-hidden="true">
        <div className="cpr-sk-bars">{[46, 72, 58, 88, 64, 40].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
      </div>
    );
  }
  if (type === 'cards') {
    return (
      <div className="cpr-skel" aria-hidden="true">
        <div className="cpr-sk-cardrow">{[0, 1].map((c) => (
          <div className="cpr-sk-card" key={c}>
            <span className="cpr-sk-line" style={{ width: '60%', height: 13 }} />
            <span className="cpr-sk-line" style={{ width: '90%' }} />
            <span className="cpr-sk-line" style={{ width: '75%' }} />
            <span className="cpr-sk-line" style={{ width: '84%', marginBottom: 0 }} />
          </div>
        ))}</div>
      </div>
    );
  }
  if (type === 'table') {
    return (
      <div className="cpr-skel cpr-skel-table" aria-hidden="true">
        {[0, 1, 2, 3].map((r) => (
          <div className="cpr-sk-trow" key={r}>
            <span className="cpr-sk-line" style={{ flex: 2 }} />
            <span className="cpr-sk-line cpr-sk-accent" style={{ flex: 1 }} />
            <span className="cpr-sk-line" style={{ flex: 1 }} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="cpr-skel" aria-hidden="true">
      <div className="cpr-sk-prose">{[92, 86, 97, 74, 89, 63].map((w, i) => <span className="cpr-sk-line" key={i} style={{ width: `${w}%` }} />)}</div>
    </div>
  );
}

// Secure-blur teaser for a locked section: a fabricated skeleton (blurred) under
// a scrim, with a crisp foreground (lock chip + one-liner + Unlock CTA).
function LockedSectionBlur({ section, t, onUpgrade }) {
  const oneLiner = t(`research.lock_${section.kind}`, t('research.lockGeneric'));
  return (
    <section
      className="card cpr-section cpr-lsec"
      id={`cpr-${section.kind}`}
      aria-label={`${section.title} — ${t('research.lockedAria', 'locked, unlock with a paid plan')}`}
    >
      <h3 className="cpr-section-h">{section.title}</h3>
      <div className="cpr-lbody">
        <LockSkeleton kind={section.kind} />
        <div className="cpr-scrim" />
        <div className="cpr-lockfg">
          <span className="cpr-lock-chip"><Icon name="lock" size={12} /> {section.title}</span>
          <p className="cpr-lock-one">{oneLiner}</p>
          <button type="button" className="cpr-unlock-cta" onClick={onUpgrade}>
            <Icon name="sparkles" size={13} /> {t('research.upCta')}
          </button>
        </div>
      </div>
    </section>
  );
}

// First sentence of a section body, stripped of citation markers + markdown —
// the "materializing" preview line on the progress section cards.
function firstSentence(md) {
  const clean = String(md || '').replace(/\[\^src_[a-z0-9]+\]/g, '').replace(/[#*_>`]/g, '').replace(/\s+/g, ' ').trim();
  if (!clean) return null;
  const m = clean.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : clean).trim().slice(0, 170);
}

const DOMAIN_COLORS = ['#4A6CF7', '#0E7C66', '#B8860B', '#7A5BFF', '#1F4E79', '#9C2B2B', '#2A6FDB', '#FF6B57', '#53A318', '#C2452D'];

function unwrap(res) {
  return res?.data && typeof res.data === 'object' ? res.data : res;
}

function domainColor(domain) {
  let hash = 0;
  for (let i = 0; i < domain.length; i += 1) hash = (hash * 31 + domain.charCodeAt(i)) >>> 0;
  return DOMAIN_COLORS[hash % DOMAIN_COLORS.length];
}

function formatDate(iso, lang) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return '';
  try {
    return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(ms));
  } catch (_) {
    return '';
  }
}

/* ---------- inline markdown with citation/assumption chips ---------- */

function renderInline(text, sourceIndex, keyPrefix) {
  const nodes = [];
  // tokens: **bold** | [^src_id] | (assumption: ...)
  const pattern = /(\*\*[^*]+\*\*)|(\[\^src_[a-z0-9]+\])|(\(assumption:[^)]*\))/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<b key={`${keyPrefix}-b${i}`}>{token.slice(2, -2)}</b>);
    } else if (token.startsWith('[^')) {
      const srcId = token.slice(2, -1);
      nodes.push(<CitationChip key={`${keyPrefix}-c${i}`} srcId={srcId} sourceIndex={sourceIndex} />);
    } else {
      nodes.push(<AssumptionChip key={`${keyPrefix}-a${i}`} formula={token.slice('(assumption:'.length, -1).trim()} />);
    }
    last = match.index + token.length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function CitationChip({ srcId, sourceIndex }) {
  const { t } = useTranslation();
  const source = sourceIndex.byId.get(srcId);
  if (!source) return null;
  const number = sourceIndex.numberOf.get(srcId);
  return (
    <span className="cpr-chip cpr-chip-src" tabIndex={0}>
      <i style={{ background: domainColor(source.domain || '') }}>{(source.domain || '?')[0].toUpperCase()}</i>
      {number}
      <span className="cpr-pop" role="tooltip">
        {source.title && <span className="cpr-pop-t">{source.title}</span>}
        {source.snippet && <span className="cpr-pop-s">“{source.snippet}”</span>}
        <span className="cpr-pop-d">
          {source.url ? (
            <a href={source.url} target="_blank" rel="noreferrer">{source.domain}</a>
          ) : source.domain}
          {source.verified && <> · {t('research.verifiedTag')}</>}
        </span>
      </span>
    </span>
  );
}

function AssumptionChip({ formula }) {
  const { t } = useTranslation();
  return (
    <span className="cpr-chip cpr-chip-asm" tabIndex={0}>
      <Icon name="grid" size={9} />
      calc
      <span className="cpr-pop" role="tooltip">
        <span className="cpr-pop-t">{t('research.assumptionTag')}</span>
        {formula && <span className="cpr-pop-f">{formula}</span>}
        <span className="cpr-pop-d">{t('research.assumptionNote')}</span>
      </span>
    </span>
  );
}

// Minimal markdown for the report bodies: headings, lists, tables, paragraphs.
// Parse one value cell into {lo,hi}. A single number → lo===hi; a range
// ("2.13–2.40") → the two bounds; non-numeric → null. We NEVER fabricate: a
// range stays a RANGE (drawn as a band, not a made-up point).
function parseCell(s) {
  const str = String(s ?? '');
  const m = str.match(/(-?\d[\d.,]*)\s*[-–—]\s*(-?\d[\d.,]*)/);
  if (m) {
    const lo = parseFloat(m[1].replace(/,/g, '')); const hi = parseFloat(m[2].replace(/,/g, ''));
    if (Number.isFinite(lo) && Number.isFinite(hi)) return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
  }
  const v = parseFloat(str.replace(/,/g, '').replace(/[^0-9.\-]/g, ' ').trim());
  return Number.isFinite(v) ? { lo: v, hi: v } : null;
}
// A clean "<label> | <number-or-range>" 2-column table (>=2 rows) → chart data.
// Returns { range, points:[{label,lo,hi,value}], caption } or null (→ plain
// table). `range` true when any cell is a span (drawn as a band).
function tableData(rows) {
  if (!Array.isArray(rows) || rows.length < 3) return null; // header + >=2 data rows
  const [head, ...data] = rows;
  const points = [];
  let range = false;
  for (const r of data) {
    if (!Array.isArray(r) || r.length !== 2) return null;
    const c = parseCell(r[1]);
    if (!c) return null;
    if (c.lo !== c.hi) range = true;
    points.push({ label: String(r[0] ?? '').trim(), lo: c.lo, hi: c.hi, value: (c.lo + c.hi) / 2 });
  }
  if (points.length < 2) return null;
  // Only chart a TIME SERIES — every label must carry a year (19xx/20xx). This
  // lets any "Year | Value" table anywhere in the report chart, while a
  // categorical table ("Service | Price", "Tier | Count") stays a plain table
  // (drawing it as a trend line would be misleading).
  if (!points.every((p) => /\b(?:19|20)\d{2}\b/.test(p.label))) return null;
  return { range, points, caption: String(head?.[1] ?? '').trim() };
}
// Same value series as the hero chart? (skip drawing the primary market twice.)
function sameValues(points, skip) {
  if (!Array.isArray(points) || !Array.isArray(skip) || points.length !== skip.length) return false;
  return points.every((p, i) => String(p.label) === String(skip[i].label) && Number(p.value) === Number(skip[i].value));
}
// Per-chart headline stat: CAGR when the year span is derivable, else total
// change first→last. "CAGR" is a language-neutral acronym, so no t needed.
function seriesStat(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const cagr = deriveCagr(points.map((p) => ({ label: p.label, value: p.value })));
  if (cagr) return `CAGR ${cagr}`;
  const v0 = Number(points[0].value); const v1 = Number(points[points.length - 1].value);
  if (!(v0 > 0) || !Number.isFinite(v1)) return null;
  const d = Math.round(((v1 - v0) / v0) * 100);
  return `${d >= 0 ? '+' : ''}${d}%`;
}

// Interactive range-trend chart for tables whose values are spans ("2.13–2.40"):
// a central-estimate trend line (the midpoints) over a faint lo→hi range band,
// with whisker ticks marking each year's span, subtle gridlines for reference,
// and a hover tooltip showing the REAL range (never the midpoint as a fact).
// Reads like a normal growth chart with a "range shadow" — coherent on sparse
// or wildly-varying data, unlike scattered floating columns.
function RangeBand({ points, color }) {
  const [hi, setHi] = useState(-1);
  const W = 280; const H = 104; const pad = 8; const top = 12; const bot = 20;
  const allv = points.flatMap((p) => [p.lo, p.hi]);
  const min = Math.min(...allv); const max = Math.max(...allv); const span = max - min || 1;
  const x = (i) => pad + (points.length === 1 ? (W - pad * 2) / 2 : (i / (points.length - 1)) * (W - pad * 2));
  const y = (v) => top + (1 - (v - min) / span) * (H - top - bot);
  const topPts = points.map((p, i) => `${x(i).toFixed(1)},${y(p.hi).toFixed(1)}`);
  const botPts = points.map((p, i) => `${x(i).toFixed(1)},${y(p.lo).toFixed(1)}`).reverse();
  const band = `M ${topPts.join(' L ')} L ${botPts.join(' L ')} Z`;
  const line = `M ${points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' L ')}`;
  const grid = [0, 0.5, 1].map((g) => top + g * (H - top - bot));
  const bw = (W - pad * 2) / Math.max(points.length, 1);
  const active = hi >= 0 ? points[hi] : null;
  const fmtR = (p) => (p.lo === p.hi ? fmtNum(p.lo) : `${fmtNum(p.lo)}–${fmtNum(p.hi)}`);
  return (
    <div className="cpr-spark">
      <svg viewBox={`0 0 ${W} ${H}`} className="cpr-spark-svg" role="img" aria-label="market range" onMouseLeave={() => setHi(-1)}>
        <g className="cpr-grid">{grid.map((gy, i) => <line key={i} x1={pad} y1={gy} x2={W - pad} y2={gy} />)}</g>
        <path d={band} className="cpr-band-area" fill={color} />
        {points.map((p, i) => (p.lo !== p.hi
          ? <line key={`w${i}`} x1={x(i)} y1={y(p.hi)} x2={x(i)} y2={y(p.lo)} className="cpr-whisk" stroke={color} />
          : null))}
        <path d={line} className="cpr-spark-line" stroke={color} fill="none" />
        {hi >= 0 && <line x1={x(hi)} y1={top} x2={x(hi)} y2={H - bot} className="cpr-spark-guide" />}
        {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.value)} r={hi === i ? 4 : 2.8} fill={color} className="cpr-spark-dot" />)}
        {points.map((p, i) => <rect key={`h${i}`} x={x(i) - bw / 2} y="0" width={bw} height={H} fill="transparent" onMouseEnter={() => setHi(i)} />)}
        {active && (() => {
          const tx = Math.max(30, Math.min(W - 30, x(hi)));
          const ty = Math.max(11, y(active.hi) - 7);
          const txt = fmtR(active);
          return (
            <g className="cpr-spark-tip" pointerEvents="none">
              <rect x={tx - Math.max(26, txt.length * 2.8)} y={ty - 11} width={Math.max(52, txt.length * 5.6)} height="15" rx="3" className="cpr-spark-tip-bg" />
              <text x={tx} y={ty} className="cpr-spark-tip-t">{txt}</text>
            </g>
          );
        })()}
      </svg>
      <div className="cpr-spark-x">{points.map((p, i) => <span key={i} className={hi === i ? 'on' : ''}>{p.label}</span>)}</div>
    </div>
  );
}

// A table's chart: caption (the value column header, e.g. "Market Size (USD bn)")
// + a stat chip, then a growth line (plain numbers) or a band (ranges).
function TableChart({ data, color }) {
  const stat = seriesStat(data.points);
  return (
    <div className="cpr-tbc">
      {(data.caption || stat) && (
        <div className="cpr-tbc-h">
          <span className="cpr-tbc-cap">{data.caption}</span>
          {stat && <span className="cpr-tbc-stat mono">{stat}</span>}
        </div>
      )}
      {data.range
        ? <RangeBand points={data.points} color={color} />
        : <GrowthSpark series={data.points.map((p) => ({ label: p.label, value: p.value }))} color={color} />}
    </div>
  );
}

// A TAM/SAM/SOM "Market Segmentation" table (every row label is a TAM/SAM/SOM
// tier) → tier cards instead of a raw table. Extreme magnitudes (230 vs 0.03)
// make a proportional bar useless, so cards carry the value + stage % + the
// derivation note. Returns [{key,label,value,note}] or null (→ normal handling).
function segmentationData(rows) {
  if (!Array.isArray(rows) || rows.length < 3) return null;
  const [head, ...data] = rows;
  const tiers = [];
  for (const r of data) {
    if (!Array.isArray(r) || r.length < 2) return null;
    const seg = String(r[0] ?? '').trim();
    const m = /\b(tam|sam|som)\b/i.exec(seg);
    if (!m) return null; // every row must be a TAM/SAM/SOM tier
    const v = parseFloat(String(r[1] ?? '').replace(/,/g, '').replace(/[^0-9.\-]/g, ' ').trim());
    tiers.push({
      key: m[1].toUpperCase(),
      label: seg.replace(/\b(tam|sam|som)\b/i, '').replace(/^[\s([]+|[\s)\]]+$/g, '').trim(),
      value: Number.isFinite(v) ? v : null,
      note: r[2] != null ? String(r[2]).trim() : '',
    });
  }
  if (tiers.length < 2 || !tiers.some((o) => o.value != null)) return null;
  const unit = String(head?.[1] ?? '').replace(/value/i, '').replace(/[()]/g, '').trim();
  return { tiers, unit };
}

function SegmentCards({ tiers, unit, color, sourceIndex }) {
  return (
    <div className="cpr-seg">
      {tiers.map((r, i) => {
        const prev = i > 0 ? tiers[i - 1].value : null;
        // Share of the previous tier. TAM→SOM ratios can be tiny (sub-1%); keep a
        // decimal there so an honest 0.26% never collapses to a misleading "0%".
        const ratio = (prev && prev > 0 && r.value != null) ? (r.value / prev) * 100 : null;
        const pct = ratio == null ? null : (ratio >= 1 ? Math.round(ratio).toString() : ratio.toFixed(ratio >= 0.1 ? 1 : 2));
        const arrow = ratio == null ? '' : (ratio > 100 ? '↑' : '↓'); // TAM≥SAM≥SOM normally; ↑ only if source data inverts
        return (
          <div key={i} className="cpr-seg-card" style={{ borderTopColor: color }}>
            <div className="cpr-seg-top">
              <span className="cpr-seg-k">{r.key}</span>
              {pct != null && <span className="cpr-seg-pct">{arrow} {pct}%</span>}
            </div>
            <div className="cpr-seg-v mono">{r.value != null ? fmtNum(r.value) : '—'}{r.value != null && unit ? <span className="cpr-seg-u"> {unit}</span> : null}</div>
            {r.label && <div className="cpr-seg-lbl">{r.label}</div>}
            {r.note && <div className="cpr-seg-note">{renderInline(r.note, sourceIndex, `seg-${i}`)}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Pull {tam,sam,som,unit} out of the FIRST TAM/SAM/SOM segmentation table found
// in a body's markdown (reuses the segmentationData parser). The structured
// metrics extractor (research-worker) is best-effort and often leaves sam/som
// null even when the report body states them in a Market Segmentation table.
function segFromBody(bodyMd) {
  if (!bodyMd || typeof bodyMd !== 'string') return null;
  let table = null;
  const resolve = () => {
    if (!table) return null;
    const seg = segmentationData(table);
    table = null;
    if (!seg) return null;
    const o = { unit: seg.unit || '' };
    for (const tr of seg.tiers) {
      const k = String(tr.key).toLowerCase();
      if (tr.value != null && (k === 'tam' || k === 'sam' || k === 'som')) o[k] = tr.value;
    }
    return (o.tam != null || o.sam != null || o.som != null) ? o : null;
  };
  for (const raw of bodyMd.split('\n')) {
    const tt = raw.trim();
    if (/^\|.*\|$/.test(tt)) {
      if (/^\|[\s:|-]+\|$/.test(tt)) continue; // separator row
      const cells = tt.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      (table = table || []).push(cells);
    } else if (table) {
      const r = resolve();
      if (r) return r;
    }
  }
  return resolve();
}

// Backfill market.sam/som (and tam) from the body's TAM/SAM/SOM segmentation
// table when the structured extractor left them null — so the exec stats, the
// funnel and the PDF all surface the figures the report genuinely contains.
// NEVER overwrites a present structured value; only backfills NULLs, and only
// when the segmentation's TAM matches the structured TAM (same scale/unit) so
// units can never mix. Pure: returns the same object when nothing to add.
function enrichMarket(market, sections) {
  if (!market) return market;
  if (market.sam != null && market.som != null) return market; // already complete
  let seg = null;
  for (const s of (sections || [])) {
    if (!s || !s.bodyMd) continue;
    const found = segFromBody(s.bodyMd);
    if (found) { seg = found; break; }
  }
  if (!seg) return market;
  const out = { ...market };
  if (market.tam != null) {
    // structured TAM exists → only trust the table if its TAM is the same scale
    if (seg.tam == null) return market;
    const ratio = seg.tam / market.tam;
    if (!(ratio > 0.8 && ratio < 1.25)) return market; // scale/unit mismatch → don't mix
    if (out.sam == null && seg.sam != null) out.sam = seg.sam;
    if (out.som == null && seg.som != null) out.som = seg.som;
    if (!out.unit && seg.unit) out.unit = seg.unit; // TAM-match confirmed same unit
  } else {
    // no structured TAM → adopt the report's own table wholesale
    if (seg.tam != null) out.tam = seg.tam;
    if (out.sam == null && seg.sam != null) out.sam = seg.sam;
    if (out.som == null && seg.som != null) out.som = seg.som;
    if (!out.unit && seg.unit) out.unit = seg.unit;
  }
  return out;
}

function ResearchMarkdown({ body, sourceIndex, chartTables = false, skipSeries = null, chartPalette }) {
  const palette = (Array.isArray(chartPalette) && chartPalette.length) ? chartPalette : ['#7A5BFF'];
  let chartIdx = 0; // assigned during render so each table chart gets a distinct colour
  const blocks = useMemo(() => {
    const lines = (body || '').split('\n');
    const out = [];
    let paragraph = [];
    let list = [];
    let table = [];
    const flushParagraph = () => { if (paragraph.length) { out.push({ type: 'p', text: paragraph.join(' ') }); paragraph = []; } };
    const flushList = () => { if (list.length) { out.push({ type: 'ul', items: list }); list = []; } };
    const flushTable = () => { if (table.length) { out.push({ type: 'table', rows: table }); table = []; } };
    for (const raw of lines) {
      const line = raw.trimEnd();
      const trimmed = line.trim();
      if (!trimmed) { flushParagraph(); flushList(); flushTable(); continue; }
      if (trimmed.startsWith('|')) {
        flushParagraph(); flushList();
        if (!/^\|[\s\-:|]+\|$/.test(trimmed)) {
          table.push(trimmed.split('|').slice(1, -1).map((cell) => cell.trim()));
        }
        continue;
      }
      flushTable();
      const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
      if (heading) {
        flushParagraph(); flushList();
        out.push({ type: 'h', depth: heading[1].length, text: heading[2] });
        continue;
      }
      if (/^[-*●]\s+/.test(trimmed)) {
        flushParagraph();
        list.push(trimmed.replace(/^[-*●]\s+/, ''));
        continue;
      }
      if (/^\d+\.\s+/.test(trimmed)) {
        flushParagraph();
        list.push(trimmed);
        continue;
      }
      flushList();
      paragraph.push(trimmed);
    }
    flushParagraph(); flushList(); flushTable();
    return out;
  }, [body]);

  return (
    <div className="cpr-md">
      {blocks.map((block, index) => {
        const key = `blk-${index}`;
        if (block.type === 'h') {
          return <h4 key={key} className={`cpr-md-h cpr-md-h${block.depth}`}>{renderInline(block.text, sourceIndex, key)}</h4>;
        }
        if (block.type === 'ul') {
          return (
            <ul key={key} className="cpr-md-ul">
              {block.items.map((item, j) => <li key={`${key}-${j}`}>{renderInline(item, sourceIndex, `${key}-${j}`)}</li>)}
            </ul>
          );
        }
        if (block.type === 'table') {
          // TAM/SAM/SOM "Market Segmentation" table → tier cards (replaces the raw
          // table; the values span orders of magnitude, so cards beat a bar/line).
          const seg = chartTables ? segmentationData(block.rows) : null;
          if (seg) {
            return (
              <div key={key} className="cpr-tblchart">
                <SegmentCards tiers={seg.tiers} unit={seg.unit} color={palette[0]} sourceIndex={sourceIndex} />
              </div>
            );
          }
          const [head, ...rows] = block.rows;
          // A clean year-by-year (or label→number/range) table gets a small chart
          // above it — a growth line for plain numbers, a band for ranges — unless
          // it's the same series the hero MarketViz already charts (skipSeries).
          // Each chart cycles a distinct palette colour (not the hero's primary).
          const data = chartTables ? tableData(block.rows) : null;
          const showChart = data && !(skipSeries && sameValues(data.points, skipSeries));
          const color = showChart ? palette[(chartIdx++ + 1) % palette.length] : null;
          const tbl = (
            // AI-authored tables have no column-count ceiling — scroll inside
            // their own container on narrow screens instead of bleeding out.
            <div className="cpr-tblwrap">
              <table className="cpr-dtab">
                {head && <thead><tr>{head.map((cell, j) => <th key={j}>{renderInline(cell, sourceIndex, `${key}-h${j}`)}</th>)}</tr></thead>}
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>{row.map((cell, ci) => <td key={ci} className={/^[€$%\d]/.test(cell) ? 'num' : ''}>{renderInline(cell, sourceIndex, `${key}-${ri}-${ci}`)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          if (!showChart) return <div key={key}>{tbl}</div>;
          return (
            <div key={key} className="cpr-tblchart">
              <div className="cpr-tblchart-viz"><TableChart data={data} color={color} /></div>
              {tbl}
            </div>
          );
        }
        return <p key={key}>{renderInline(block.text, sourceIndex, key)}</p>;
      })}
    </div>
  );
}

// Scroll-spy: which section is currently at the reading line. Position-based
// (not IntersectionObserver): the IO callback only reports CHANGED entries, so
// when section A left the band while section B had entered it long before, no
// event fired and the highlight stuck on A — the exact bug users saw. Instead,
// on every scroll (document-level CAPTURE listener — the app scrolls an inner
// container, and scroll events don't bubble) recompute "the last section whose
// top is above the reading line" from live rects. 12 rect reads per rAF — cheap.
function useScrollSpy(ids) {
  const [active, setActive] = useState(null);
  const key = ids.join('|');
  useEffect(() => {
    if (!ids.length) return undefined;
    setActive((a) => a || ids[0]);
    let raf = 0;
    const recompute = () => {
      raf = 0;
      const line = 110; // just below the sticky chrome
      let current = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = id;
        else break; // ids follow document order
      }
      setActive(current || ids[0]);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(recompute); };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll);
    recompute();
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return active;
}

/* ---------- main tab ---------- */

// The worker stores intake verbatim and feeds the whole object to the analyst
// prompts; the query planner reads `business` + `geography`. The profile has no
// dedicated geography field yet, so the timezone city is the honest v1 hint.
function buildIntake(profile) {
  if (!profile) return {};
  const description = String(profile.businessDescription || '').trim();
  const querySeed = description.split(/[.!?\n]/)[0].trim().slice(0, 120) || profile.businessName || '';
  const tzCity = String(profile.timezone || '').includes('/')
    ? profile.timezone.split('/').pop().replace(/_/g, ' ')
    : '';
  const intake = {
    business: querySeed || undefined,
    brandName: profile.businessName || undefined,
    businessDescription: description || undefined,
    productOrService: profile.productOrService || undefined,
    targetAudience: profile.targetAudience || undefined,
    offer: profile.offer || undefined,
    positioning: profile.positioning || undefined,
    uniqueSellingPoints: profile.uniqueSellingPoints?.length ? profile.uniqueSellingPoints : undefined,
    buyerPains: profile.buyerPains?.length ? profile.buyerPains : undefined,
    website: profile.websiteUrl || undefined,
    geography: tzCity || undefined,
    timezone: profile.timezone || undefined,
    language: profile.language || undefined,
  };
  return Object.fromEntries(Object.entries(intake).filter(([, value]) => value !== undefined));
}

export function ResearchTab({ brandProfile }) {
  const { t, i18n } = useTranslation();
  const { openUpgrade } = useUpgrade();
  const dash = useDashboardData() || {};
  const creationBlocked = creationAccessState(dash) !== 'allowed';
  const noBrand = dash.setupStatus?.brand === 'missing';
  // The empty state = zero reports = a free user's one bootstrap is available
  // (the research worker allows a free run only while no ready report exists),
  // so "Run research" is offered to everyone here; the server enforces the
  // "first research free, refresh is paid" rule + the monthly cap.
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);   // full report payload or null
  const [reports, setReports] = useState([]);   // version history (list summaries)
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  // TASK-331 owner finding (staging): a user who skipped onboarding (Quick
  // brand: name only; businessDescription/targetAudience still hold
  // onboarding's placeholder strings) could click "Start market research"
  // straight into a real run over empty brand input, burning the plan's
  // monthly quota. `gate` mirrors the client-side pre-check; `brandIncomplete`
  // is the server-side backstop (research-worker's 422 brand_incomplete) for
  // whatever that mirror missed — see startRun's catch block below.
  const gate = useMemo(() => researchStartGate(brandProfile), [brandProfile]);
  const [brandIncomplete, setBrandIncomplete] = useState(false);
  useEffect(() => { if (!gate.blocked) setBrandIncomplete(false); }, [gate.blocked]);
  const showBrandGate = gate.blocked || brandIncomplete;
  const pollRef = useRef(null);
  // Held selection so polling/reloads don't yank the user off a chosen past
  // version; cleared on a new run so it follows the freshly-started report.
  const selectedIdRef = useRef(null);

  useEffect(() => { dash.ensureDashboardData?.(); }, [dash.ensureDashboardData]);

  // Best-effort primary-logo fetch so the research header reads as "your
  // brand's research". Only when Brand Kit is on; failure → no logo (graceful,
  // falls back to the brand initial).
  useEffect(() => {
    if (!brandKitEnabled()) return undefined;
    let cancelled = false;
    api.brandAssets.list()
      .then((res) => {
        const data = res?.data ?? res;
        const logo = (data?.assets || []).find((a) => a.id === data?.primaryLogoId);
        if (!cancelled) setBrandLogoUrl(logo?.url || null);
      })
      .catch(() => { /* no logo */ });
    return () => { cancelled = true; };
  }, []);

  const brandName = (brandProfile?.businessName || '').trim();
  const brandColors = Array.isArray(brandProfile?.brandColors) ? brandProfile.brandColors.filter(Boolean).slice(0, 4) : [];

  const load = useCallback(async (preferredId) => {
    try {
      const listRes = unwrap(await api.research.list());
      const list = listRes?.reports || [];
      setReports(list);
      // Pick which report to show: an explicit pick (dropdown) wins, then a
      // still-valid held selection, else the newest run (list is created_at DESC).
      const keep = preferredId || selectedIdRef.current;
      const wantId = (keep && list.some((r) => r.id === keep)) ? keep : (list[0]?.id || null);
      if (!wantId) {
        setReport(null); setSelectedReportId(null); selectedIdRef.current = null;
        return null;
      }
      selectedIdRef.current = wantId;
      setSelectedReportId(wantId);
      const repRes = unwrap(await api.research.get(wantId));
      setReport(repRes?.report || null);
      setError(null);
      return repRes?.report || null;
    } catch (err) {
      setError(err?.message || 'failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while a run is active so stages/counters tick live.
  useEffect(() => {
    const active = report && ACTIVE_STATUSES.includes(report.status);
    if (!active) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(() => { api.invalidateCache?.('/api/research'); load(); }, POLL_MS);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [report, load]);

  const startRun = async () => {
    if (creating || noBrand || gate.blocked || creationBlocked) return;
    setCreating(true);
    try {
      await api.research.create({ trigger: 'manual', language: i18n.language, intake: buildIntake(brandProfile) });
      api.invalidateCache?.('/api/research');
      selectedIdRef.current = null; // follow the freshly started/refreshed run
      setBrandIncomplete(false);
      await load();
    } catch (err) {
      // TASK-331: the brand gate above already hides this button once
      // researchStartGate() sees the active brand isn't strategy-ready — this
      // is the server-side backstop for whatever that client-side mirror
      // missed (a stale brandProfile prop, a race right after onboarding,
      // etc.). research-worker rejects with 422 brand_incomplete instead of
      // silently running a real report over empty input and burning the
      // plan's monthly quota on nothing — render the same calm gate rather
      // than a generic error.
      if (err?.status === 422 && err?.code === 'brand_incomplete') {
        setBrandIncomplete(true);
        setError(null);
      } else {
        // PLAN_LAPSED first — the full research report a lapsed org already
        // paid for stays readable (D6); only RUNNING research is blocked,
        // and "quota reached" is the wrong framing for that.
        setError(err?.code === 'PLAN_LAPSED' ? t('paymentIssue.lapsedBody') : err?.status === 402 ? t('research.quotaReached') : err?.message || 'failed');
      }
    } finally {
      setCreating(false);
    }
  };

  const refreshRun = async () => {
    if (!report || creating || noBrand || creationBlocked) return;
    setCreating(true);
    try {
      await api.research.refresh(report.id);
      api.invalidateCache?.('/api/research');
      selectedIdRef.current = null; // follow the freshly started/refreshed run
      await load();
    } catch (err) {
      // TASK-331: PLAN_LAPSED first — the full research report a lapsed org
      // already paid for stays readable (D6); only RUNNING research is
      // blocked, and "quota reached" is the wrong framing for that.
      setError(err?.code === 'PLAN_LAPSED' ? t('paymentIssue.lapsedBody') : err?.status === 402 ? t('research.quotaReached') : err?.message || 'failed');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <>
        {noBrand && <NoBrandGuard surface="research" />}
        <div className="cpr-loading"><span className="dot pulse" /> {t('research.loading')}</div>
      </>
    );
  }
  if (!report) {
    return (
      <>
        <CreationAccessNotice />
        {noBrand && <NoBrandGuard surface="research" />}
        {showBrandGate
          ? <ResearchBrandGate primary={gate.primary} />
          : <EmptyState onRun={startRun} creating={creating} creationBlocked={creationBlocked} error={error} noBrand={noBrand} />}
      </>
    );
  }
  const brandTie = { brandLogoUrl, brandName, brandColors };
  if (ACTIVE_STATUSES.includes(report.status)) {
    return <>{noBrand && <NoBrandGuard surface="research" />}<ProgressState report={report} {...brandTie} /></>;
  }
  if (report.status === 'failed') {
    return (
      <>
        <CreationAccessNotice />
        {noBrand && <NoBrandGuard surface="research" />}
        {showBrandGate
          ? <ResearchBrandGate primary={gate.primary} />
          : <FailedState report={report} onRetry={startRun} creating={creating} creationBlocked={creationBlocked} error={error} noBrand={noBrand} />}
      </>
    );
  }
  return (
    <>
      <CreationAccessNotice />
      {noBrand && <NoBrandGuard surface="research" />}
      <ReportView
        report={report}
        reports={reports}
        selectedReportId={selectedReportId}
        onSelectReport={(id) => load(id)}
        lang={i18n.language}
        onRefresh={refreshRun}
        refreshing={creating}
        creationBlocked={creationBlocked}
        noBrand={noBrand}
        error={error}
        onUpgrade={() => openUpgrade({ feature: 'research' })}
        {...brandTie}
      />
    </>
  );
}

/* ---------- brand gate (TASK-331) ---------- */

// Calm "finish your brand basics first" empty state, shown in place of the
// marketing empty state / failed-run retry whenever researchStartGate()
// says the active brand isn't strategy-ready yet (or the server rejected
// the run with 422 brand_incomplete). Reuses the exact CTA keys and
// bare-vs-not-bare routing decideActivationProgress's own empty-brand
// branch already uses for the dashboard card, so the two surfaces never
// disagree on which path — guided onboarding vs manual edit — to offer
// first. One primary CTA; the other path is a plain text link.
function ResearchBrandGate({ primary }) {
  const { t } = useTranslation();
  const editStrategyTo = '/app/plan/edit-strategy#business';
  return (
    <div className="cpr-empty">
      <div className="cpr-orb" aria-hidden>
        <span className="cpr-orb-ring"><span className="cpr-orb-sat" /></span>
        <span className="cpr-orb-core"><Icon name="sparkles" size={22} /></span>
      </div>
      <h2 className="cpr-empty-h">{t('research.brandGateTitle')}</h2>
      <p className="cpr-empty-lede">{t('research.brandGateBody')}</p>
      {primary === 'onboarding' ? (
        <>
          <Link to="/onboarding" className="btn btn-primary cpr-cta">
            {t('activationProgress.startOnboardingCta')} <Icon name="arrowRight" size={13} />
          </Link>
          <Link to={editStrategyTo} className="fr-cl-retry">{t('activationProgress.completeBrandManuallyCta')}</Link>
        </>
      ) : (
        <Link to={editStrategyTo} className="btn btn-primary cpr-cta">
          {t('activationProgress.completeBrandCta')} <Icon name="arrowRight" size={13} />
        </Link>
      )}
    </div>
  );
}

/* ---------- empty ---------- */

function EmptyState({ onRun, creating, creationBlocked, error, noBrand }) {
  const { t } = useTranslation();
  return (
    <div className="cpr-empty">
      <div className="cpr-orb" aria-hidden>
        <span className="cpr-orb-ring"><span className="cpr-orb-sat" /></span>
        <span className="cpr-orb-core"><Icon name="search" size={22} /></span>
      </div>
      <h2 className="cpr-empty-h">{t('research.emptyTitle1')}<br /><span className="cpr-ac">{t('research.emptyTitle2')}</span></h2>
      <p className="cpr-empty-lede">{t('research.emptyLede')}</p>
      <div className="cpr-bullets">
        {[1, 2, 3].map((n) => (
          <div key={n} className="cpr-bullet">
            <Icon name="check" size={13} />
            <span><b>{t(`research.bullet${n}b`)}</b> {t(`research.bullet${n}`)}</span>
          </div>
        ))}
      </div>
      <button className="btn btn-primary cpr-cta" type="button" disabled={creating || noBrand || creationBlocked} onClick={onRun} aria-describedby={noBrand ? 'no-brand-guard-hint-research' : undefined} title={noBrand ? t('noBrandGuard.disabledHint') : undefined}>
        {creating ? t('research.starting') : t('research.runCta')} <Icon name="arrowRight" size={13} />
      </button>
      {error && <span className="cpr-error">{error}</span>}
      <span className="cpr-note">{t('research.emptyNote')}</span>
    </div>
  );
}

/* ---------- progress ---------- */

function stageStates(report) {
  const runs = report.runs || [];
  const doneSteps = new Set(runs.filter((r) => r.status === 'succeeded').map((r) => r.step));
  const phaseRank = { queued: 0, gathering: 0, analyzing: 1, synthesizing: 3, verifying: 4, ready: 5, failed: 5 };
  const rank = phaseRank[report.status] ?? 0;
  const states = STAGES.map((stage, index) => {
    const done = runs.some((r) => r.status === 'succeeded' && stage.match(r))
      || (index === 0 && doneSteps.has('gather'))
      || rank > indexToRank(index);
    const now = !done && rank === indexToRank(index);
    return { ...stage, state: done ? 'done' : now ? 'now' : 'todo' };
  });
  // Between stages (e.g. every analyze run done but synthesize not started)
  // nothing matches 'now' and the header used to fall back to stage 1 —
  // showing "collecting sources" long after collection finished. The first
  // not-done stage IS the current one while the run is active.
  if (rank < 5 && !states.some((s) => s.state === 'now')) {
    const next = states.find((s) => s.state === 'todo');
    if (next) next.state = 'now';
  }
  return states;
}

function indexToRank(index) {
  // collect=0 maps to gathering(0); audience/competitors map to analyzing(1);
  // synthesize=3; verify=4 — mirrors phaseRank above.
  return [0, 1, 1, 3, 4][index] ?? 0;
}

function ProgressState({ report, brandLogoUrl, brandName = '' }) {
  const { t } = useTranslation();
  const stages = stageStates(report);
  const current = stages.find((s) => s.state === 'now') || stages[0];
  const sources = report.sources || [];
  const domains = [...new Set(sources.map((s) => s.domain).filter(Boolean))];
  const sectionsReady = new Set((report.sections || []).map((s) => s.kind));
  const expectedKinds = report.depth === 'lite' ? ['methodology', 'audience'] : KIND_ORDER;
  const entitled = report.entitled === true;

  return (
    <div className="cpr-progress">
      <div className="cpr-prog-head">
        <div className="cpr-prog-head-l">
          {(brandLogoUrl || brandName) && <BrandTile logoUrl={brandLogoUrl} name={brandName} size="sm" />}
          <h2 className="cpr-prog-title">
            {t('research.progressTitle')} <span className="cpr-ac">{t(`research.${current.key}`).toLowerCase()}</span>
          </h2>
        </div>
        <span className="pill pill-info"><span className="dot pulse" /> {t('research.runningPill')}</span>
      </div>
      <p className="cpr-prog-sub">{t('research.progressSub')}</p>

      <div className="card cpr-stages">
        {stages.map((stage) => {
          const hasCount = !!stage.count;
          const cnt = hasCount ? stage.count(report) : 0;
          const detail = stage.state === 'done'
            ? ((!hasCount || cnt > 0) ? t(`research.${stage.key}Done`, { count: cnt }) : t('research.stageDone'))
            : stage.state === 'now' ? t(`research.${stage.key}Active`) : t('research.stageWaiting');
          return (
            <div key={stage.id} className={`cpr-stage is-${stage.state}`}>
              <span className="cpr-stage-ico">
                <Icon name={STAGE_ICON[stage.id] || 'refresh'} size={12} />
              </span>
              <div className="cpr-stage-main">
                <span className="cpr-stage-l">{t(`research.${stage.key}`)}</span>
                <span className="cpr-stage-d">{detail}</span>
              </div>
              <span className="cpr-stage-meta">{stage.eta}</span>
            </div>
          );
        })}
      </div>

      <div className="cpr-counters">
        <div className="card cpr-counter">
          <span className="cpr-c-l">{t('research.sourcesFound')}</span>
          <span className="cpr-c-v">{sources.length}</span>
          <span className="cpr-c-s">{t('research.sourcesSub')}</span>
        </div>
        <div className="card cpr-counter cpr-counter-wide">
          <span className="cpr-c-l">{t('research.uniqueDomains')}</span>
          <span className="cpr-c-v">{domains.length}</span>
          <div className="cpr-doms">
            {domains.slice(0, 14).map((domain) => (
              <span key={domain} className="cpr-dch">
                <i style={{ background: domainColor(domain) }}>{domain[0].toUpperCase()}</i>{domain}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="cpr-secs">
        {(() => {
          // Free users see the FULL report shape forming: open sections
          // materialize, premium (locked) ones show a blurred teaser from the
          // start — so the value is visible mid-run. Entitled users keep the
          // compact materializing preview.
          const deckKinds = (entitled ? expectedKinds : KIND_ORDER).filter((kind) => kind !== 'methodology');
          const shown = entitled ? deckKinds.slice(0, 4) : deckKinds;
          return shown.map((kind) => {
            const lockedForFree = !entitled && kind !== 'audience' && !!LOCK_ARTEFACT[kind];
            if (lockedForFree) {
              return (
                <div key={kind} className="card cpr-sec cpr-sec-locked">
                  <h3>
                    <Icon name="lock" size={13} />
                    {t(`research.kind_${kind}`)}
                    <span className="cpr-sec-tag cpr-sec-tag-lock">{t('research.secLocked', 'Locked')}</span>
                  </h3>
                  <div className="cpr-sec-blur" aria-hidden="true">
                    <span className="cpr-sk" style={{ width: '82%' }} />
                    <span className="cpr-sk" style={{ width: '64%' }} />
                    <span className="cpr-sk" style={{ width: '74%' }} />
                  </div>
                </div>
              );
            }
            const sec = (report.sections || []).find((s) => s.kind === kind);
            const ready = !!sec;
            const snippet = sec ? firstSentence(sec.bodyMd) : null;
            return (
              <div key={kind} className={`card cpr-sec ${ready ? 'is-ready' : ''}`}>
                <h3>
                  <Icon name={STAGE_ICON[kind === 'channels' || kind === 'content_angles' ? 'collect' : kind] || 'target'} size={13} />
                  {t(`research.kind_${kind}`)}
                  <span className="cpr-sec-tag">{ready ? t('research.secReady') : t('research.secWaiting')}</span>
                </h3>
                {ready
                  ? (snippet ? <p className="cpr-sec-line">{snippet}</p> : null)
                  : <><span className="cpr-sk" style={{ width: '82%' }} /><span className="cpr-sk" style={{ width: '64%' }} /><span className="cpr-sk" style={{ width: '74%' }} /></>}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

/* ---------- failed ---------- */

function FailedState({ report, onRetry, creating, creationBlocked, error, noBrand }) {
  const { t } = useTranslation();
  return (
    <div className="cpr-empty">
      <h2 className="cpr-empty-h">{t('research.failedTitle')}</h2>
      <p className="cpr-empty-lede">{report.errorCode === 'cancelled' ? t('research.cancelledBody') : t('research.failedBody')}</p>
      <button className="btn btn-primary cpr-cta" type="button" disabled={creating || noBrand || creationBlocked} onClick={onRetry} aria-describedby={noBrand ? 'no-brand-guard-hint-research' : undefined} title={noBrand ? t('noBrandGuard.disabledHint') : undefined}>
        {creating ? t('research.starting') : t('research.retryCta')}
      </button>
      {error && <span className="cpr-error">{error}</span>}
    </div>
  );
}

/* ---------- charts (structured metrics → SVG, no deps) ----------
   Render ONLY when the worker derived clean numerics (report.metrics). The
   backend forbids invented numbers and ships null when nothing is derivable,
   so a missing chart means "no data", never a broken widget. */

const CHART_FALLBACK = ['#7A5BFF', '#4FC3FF', '#E455CF', '#2DD4A7', '#F5A623', '#5B8DEF'];
export function chartColors(brandColors) {
  const out = (brandColors || []).filter(Boolean).slice(0, 6);
  for (const c of CHART_FALLBACK) { if (out.length >= 6) break; if (!out.includes(c)) out.push(c); }
  return out.length ? out : CHART_FALLBACK;
}
function fmtNum(n) {
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  // Keep 2 decimals for small values so a tiny-but-present figure (e.g. a
  // modeled SOM of 0.04) shows as "0.04", not a misleading "0".
  const digits = abs > 0 && abs < 1 ? 2 : abs < 10 ? 1 : 0;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

// The exec summary must be plain prose. Strip any markdown table / list / heading
// lines the model may have echoed from the section digest, so raw "| … |" rows
// (or '#'/'-' artifacts) never leak into the short version.
function plainSummary(s) {
  if (!s) return '';
  return String(s)
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('|') && !l.startsWith('#') && !/^[-|:\s]+$/.test(l))
    .join(' ')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function MetricsBadge({ basis, t }) {
  const sourced = basis === 'sourced';
  return (
    <span className={`cpr-basis ${sourced ? 'cpr-basis-src' : 'cpr-basis-mod'}`} title={t(sourced ? 'research.basisSourcedTip' : 'research.basisModeledTip')}>
      <Icon name={sourced ? 'check' : 'sparkles'} size={10} /> {t(sourced ? 'research.basisSourced' : 'research.basisModeled')}
    </span>
  );
}

function DonutChart({ segments, colors }) {
  const [hi, setHi] = useState(-1);
  const total = segments.reduce((s, x) => s + x.share, 0) || 1;
  const R = 46;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const top = segments.reduce((a, b) => (b.share > a.share ? b : a), segments[0]);
  const center = hi >= 0 ? segments[hi] : top;
  const pct = (s) => Math.round((s / total) * 100);
  return (
    <div className="cpr-donut">
      <svg viewBox="0 0 120 120" className="cpr-donut-svg" role="img" aria-label="audience split" onMouseLeave={() => setHi(-1)}>
        <circle cx="60" cy="60" r={R} className="cpr-donut-track" />
        <g transform="rotate(-90 60 60)">
          {segments.map((seg, i) => {
            const dash = (seg.share / total) * C;
            const node = (
              <circle
                key={i} cx="60" cy="60" r={R}
                className={`cpr-donut-arc${hi >= 0 && hi !== i ? ' dim' : ''}`}
                stroke={colors[i % colors.length]} strokeWidth={hi === i ? 18 : 15}
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc}
                onMouseEnter={() => setHi(i)}
              />
            );
            acc += dash;
            return node;
          })}
        </g>
        <text x="60" y="57" className="cpr-donut-c1">{pct(center.share)}%</text>
        <text x="60" y="71" className="cpr-donut-c2">{center.label.slice(0, 12)}</text>
      </svg>
      <ul className="cpr-donut-legend">
        {segments.map((seg, i) => (
          <li
            key={i} className={`cpr-leg${hi === i ? ' on' : ''}`}
            onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(-1)}
          >
            <i style={{ background: colors[i % colors.length] }} />
            <span className="cpr-leg-l" title={seg.label}>{seg.label}</span>
            <span className="cpr-leg-v mono">{pct(seg.share)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GrowthSpark({ series, color, unit }) {
  const [hi, setHi] = useState(-1);
  const W = 280;
  const H = 92;
  const pad = 8;
  const vals = series.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => pad + (series.length === 1 ? 0 : (i / (series.length - 1)) * (W - pad * 2));
  const y = (v) => H - pad - 18 - ((v - min) / span) * (H - pad * 2 - 18);
  const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `M ${x(0).toFixed(1)},${H - pad} L ${pts.join(' L ')} L ${x(series.length - 1).toFixed(1)},${H - pad} Z`;
  const band = W / series.length;
  const active = hi >= 0 ? series[hi] : null;
  return (
    <div className="cpr-spark">
      <svg viewBox={`0 0 ${W} ${H}`} className="cpr-spark-svg" role="img" aria-label="market growth" onMouseLeave={() => setHi(-1)}>
        <path d={area} className="cpr-spark-area" fill={color} />
        <path d={line} className="cpr-spark-line" stroke={color} />
        {hi >= 0 && <line x1={x(hi)} y1="14" x2={x(hi)} y2={H - pad} className="cpr-spark-guide" />}
        {series.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.value)} r={hi === i ? 4 : 2.6} fill={color} className="cpr-spark-dot" />)}
        {series.map((p, i) => (
          <rect key={`h${i}`} x={x(i) - band / 2} y="0" width={band} height={H} fill="transparent" onMouseEnter={() => setHi(i)} />
        ))}
        {active && (() => {
          const tx = Math.max(28, Math.min(W - 28, x(hi)));
          const ty = Math.max(13, y(active.value) - 9);
          const txt = `${fmtNum(active.value)}${unit ? ` ${unit}` : ''}`;
          return (
            <g className="cpr-spark-tip" pointerEvents="none">
              <rect x={tx - Math.max(24, txt.length * 2.7)} y={ty - 11} width={Math.max(48, txt.length * 5.4)} height="15" rx="3" className="cpr-spark-tip-bg" />
              <text x={tx} y={ty} className="cpr-spark-tip-t">{txt}</text>
            </g>
          );
        })()}
      </svg>
      <div className="cpr-spark-x">{series.map((p, i) => <span key={i} className={hi === i ? 'on' : ''}>{p.label}</span>)}</div>
    </div>
  );
}

// TAM→SAM→SOM funnel. The tier WIDTHS are fixed/illustrative (TAM..SOM ratios
// are usually extreme — a to-scale bar would make SAM/SOM vanish); the real
// numbers + the stage-to-stage conversion % carry the truth. One accent color
// fading per tier (premium-minimal). Each tier self-shows only when present.
function MarketFunnel({ rows, unit, color, t }) {
  const widths = ['100%', '62%', '34%'];
  const fades = [1, 0.74, 0.5];
  return (
    <div className="cpr-fn" role="img" aria-label={t('research.chartMarketTitle')}>
      {rows.map((r, i) => {
        const prev = i > 0 ? rows[i - 1] : null;
        const ratio = (prev && prev.v > 0 && r.v != null) ? (r.v / prev.v) * 100 : null;
        // Decimal for sub-1% so an honest 0.26% never reads as "0%".
        const conv = ratio == null ? null : (ratio >= 1 ? Math.round(ratio).toString() : ratio.toFixed(ratio >= 0.1 ? 1 : 2));
        return (
          <div key={r.k} className="cpr-fn-row">
            <span className="cpr-fn-k">
              {t(`research.metric_${r.k}`)}
              {conv != null && <span className="cpr-fn-conv" title={t('research.funnelConv')}>{conv}%</span>}
            </span>
            <div className="cpr-fn-track">
              <span className="cpr-fn-barwrap"><span className="cpr-fn-bar" style={{ width: widths[i], background: color, opacity: fades[i] }} /></span>
              <span className="cpr-fn-v mono">{fmtNum(r.v)}{unit ? ` ${unit}` : ''}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MarketViz({ data, colors, t }) {
  const rows = [['tam', data.tam], ['sam', data.sam], ['som', data.som]]
    .filter(([, v]) => v != null)
    .map(([k, v]) => ({ k, v }));
  if (rows.length === 0 && !data.series) return null;
  const cagr = deriveCagr(data.series);
  return (
    <div className="cpr-viz cpr-mkt">
      <div className="cpr-viz-head">
        <span className="cpr-viz-t">{t('research.chartMarketTitle')}</span>
        <div className="cpr-viz-head-r">
          {cagr && (
            <span className="cpr-stat" title={t('research.statCagrTip')}>
              <span className="cpr-stat-v mono">{cagr}</span>
              <span className="cpr-stat-k">{t('research.statCagr')}</span>
            </span>
          )}
          <MetricsBadge basis={data.basis} t={t} />
        </div>
      </div>
      {rows.length > 0 && <MarketFunnel rows={rows} unit={data.unit} color={colors[0]} t={t} />}
      {data.series && (
        <div className="cpr-spark-wrap cpr-spark-wrap-lg">
          <span className="cpr-spark-cap">{t('research.chartGrowthCap')}</span>
          <GrowthSpark series={data.series} color={colors[0]} unit={data.unit} />
        </div>
      )}
    </div>
  );
}

export function AudienceViz({ data, colors, t }) {
  if (!data.segments || data.segments.length < 2) return null;
  return (
    <div className="cpr-viz">
      <div className="cpr-viz-head">
        <span className="cpr-viz-t">{t('research.chartAudienceTitle')}</span>
        <MetricsBadge basis={data.basis} t={t} />
      </div>
      <DonutChart segments={data.segments} colors={colors} />
    </div>
  );
}

/* ============================================================
   FREE "full-length secure blur" report (entitled === false).
   The whole report at its true length: a sticky scroll-spy rail (12
   sections, open-dot vs lock-glyph), each section a full-height card with
   an Open/Locked badge, and a sticky bottom Unlock bar.
   SECURITY: only whitelisted aggregates (market / channels / audience /
   archetypes) carry real data; every premium body is a FABRICATED blurred
   skeleton — no real bodyMd, economics, competitors, positioning, angles,
   roadmap or full sources is ever rendered here.
   ============================================================ */

// The canonical 12-section spine the free report always renders at full
// length, in pack order. `role` decides the treatment; `icon` is the nav glyph.
// Empty citation index for free section bodies: matches the {byId,numberOf}
// shape CitationChip expects (a plain Map would crash on sourceIndex.byId.get).
// Free reports carry no full sources, so every [^src] chip resolves to nothing.
const EMPTY_SOURCE_INDEX = { byId: new Map(), numberOf: new Map() };

const FREE_SPINE = [
  { id: 'exec', role: 'open', icon: 'book' },
  { id: 'methodology', role: 'locked', icon: 'book', artefact: 'table' },
  { id: 'market', role: 'peek', icon: 'target', artefact: 'table' },
  { id: 'audience', role: 'open', icon: 'user' },
  { id: 'competitors', role: 'locked', icon: 'grid', artefact: 'cards' },
  { id: 'positioning', role: 'locked', icon: 'target', artefact: 'prose' },
  { id: 'economics', role: 'locked', icon: 'creditCard', artefact: 'table' }, // LOCKED — no peek (deviation from pack)
  { id: 'channels', role: 'peek', icon: 'layers', artefact: 'funnel' },
  { id: 'content_angles', role: 'locked', icon: 'sparkles', artefact: 'list' },
  { id: 'action_plan', role: 'locked', icon: 'arrowRight', artefact: 'cards' },
  { id: 'risks', role: 'locked', icon: 'alert', artefact: 'prose' },
  // sources are no longer a body section — they render as a sticky card in the
  // right column (cpr-col-side), consistent with the entitled report layout.
];

// kind → nav icon, shared by the free spine and the entitled scroll-spy so both
// menus look identical (icon + clean canonical title), not raw long section names.
const SPINE_ICON = Object.fromEntries(FREE_SPINE.map((s) => [s.id, s.icon]));

// Annualized CAGR DERIVED in the UI from market.series (first vs last point).
// No stored CAGR exists. Returns a rounded % string or null when underivable.
function deriveCagr(series) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const first = series[0];
  const last = series[series.length - 1];
  const v0 = Number(first?.value);
  const v1 = Number(last?.value);
  const y0 = parseInt(String(first?.label).replace(/[^0-9]/g, ''), 10);
  const y1 = parseInt(String(last?.label).replace(/[^0-9]/g, ''), 10);
  if (!(v0 > 0) || !(v1 > 0)) return null;
  const years = (Number.isFinite(y0) && Number.isFinite(y1) && y1 > y0) ? (y1 - y0) : (series.length - 1);
  if (years <= 0) return null;
  const cagr = (Math.pow(v1 / v0, 1 / years) - 1) * 100;
  if (!Number.isFinite(cagr)) return null;
  return `${cagr.toFixed(1)}%`;
}

// A crisp "Live data" peek chart sitting ABOVE a blurred fabricated body.
function PeekChart({ tag, t, children }) {
  return (
    <div className="cpr-peek">
      <span className="cpr-peek-tag">{tag || t('research.peekLive')}</span>
      {children}
    </div>
  );
}

// Vertical bar group (real values) used for the exec market-growth chart and
// the market TAM·SAM·SOM peek. Heights normalize to the max value.
function BarGroup({ rows, colors, unit, showUnit = true }) {
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  return (
    <div className="cpr-bargroup">
      {rows.map((r, i) => {
        const v = Number(r.value);
        const has = Number.isFinite(v);
        return (
          <div className="cpr-bg-col" key={r.label + i} title={has && unit ? `${fmtNum(v)} ${unit}` : undefined}>
            <span className="cpr-bg-v mono">{has ? fmtNum(v) : '~'}{has && unit && showUnit ? ` ${unit}` : ''}</span>
            <div className="cpr-bg-bar" style={{ height: `${has ? Math.max(8, (v / max) * 100) : 12}%`, background: colors[i % colors.length] }} />
            <span className="cpr-bg-l mono">{r.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Headline market stats (TAM / CAGR / SAM) + a growth-bar viz, shown at the top
// of the executive summary on BOTH the free and paid reports so the two never
// drift. Self-hides each piece when its data is absent (thin-niche reports).
function ExecHighlights({ market, palette, t }) {
  if (!market) return null;
  const tam = market.tam;
  const sam = market.sam;
  const unit = market.unit || '';
  const series = Array.isArray(market.series) ? market.series : [];
  const cagr = series.length >= 2 ? deriveCagr(series) : null;
  const yearSpan = series.length >= 2 ? `${series[0].label}–${series[series.length - 1].label}` : '';
  // SAM as a share of TAM; keep a decimal for sub-1% so a true 0.26% never
  // collapses to a misleading "~0%".
  const samRatio = (tam && sam) ? (sam / tam) * 100 : null;
  const samPct = samRatio == null ? null : (samRatio >= 1 ? Math.round(samRatio).toString() : samRatio.toFixed(samRatio >= 0.1 ? 1 : 2));
  const hasStats = tam != null || cagr || sam != null;
  if (!hasStats && series.length < 2) return null;
  return (
    <>
      {hasStats && (
        <div className="cpr-stats">
          {tam != null && (
            <div className="cpr-stat">
              <span className="cpr-stat-k">{t('research.statTam')}</span>
              <span className="cpr-stat-v mono">{fmtNum(tam)}{unit ? <small> {unit}</small> : null}</span>
            </div>
          )}
          {cagr && (
            <div className="cpr-stat">
              <span className="cpr-stat-k">{t('research.statCagr')}</span>
              <span className="cpr-stat-v mono">{cagr}</span>
              {yearSpan && <span className="cpr-stat-s mono">{yearSpan}</span>}
            </div>
          )}
          {sam != null && (
            <div className="cpr-stat">
              <span className="cpr-stat-k">{t('research.statSam')}</span>
              <span className="cpr-stat-v mono">{fmtNum(sam)}{unit ? <small> {unit}</small> : null}</span>
              {samPct != null && <span className="cpr-stat-s mono">~{samPct}% · TAM</span>}
            </div>
          )}
        </div>
      )}
      {series.length >= 2 && (
        <div className="cpr-viz cpr-viz-grow">
          <div className="cpr-viz-head">
            <span className="cpr-viz-t">{t('research.chartGrowthCap')}{unit ? ` · ${unit}` : ''}</span>
            {yearSpan && <span className="cpr-viz-cap mono">{yearSpan}</span>}
          </div>
          {/* Unit lives in the caption above — repeating it on every bar made
              each nowrap label ~130px wide, so 6+ bars overflowed the card. */}
          <BarGroup rows={series} colors={palette} unit={unit} showUnit={false} />
          {market.basis === 'modeled' && <span className="cpr-viz-note">{t('research.basisModeled')}</span>}
        </div>
      )}
    </>
  );
}

// Conic-gradient discovery donut from blocks.channels (cumulative stops).
function ChannelDonut({ items, colors, t }) {
  const list = [...items].filter((c) => Number.isFinite(Number(c.share))).sort((a, b) => b.share - a.share).slice(0, 6);
  const total = list.reduce((s, c) => s + Number(c.share), 0) || 1;
  let acc = 0;
  const stops = list.map((c, i) => {
    const from = (acc / total) * 100;
    acc += Number(c.share);
    const to = (acc / total) * 100;
    return `${colors[i % colors.length]} ${from}% ${to}%`;
  }).join(',');
  return (
    <div className="cpr-cdonut-row">
      <div className="cpr-cdonut" style={{ background: `conic-gradient(${stops})` }} aria-hidden="true" />
      <ul className="cpr-cdonut-legend">
        {list.map((c, i) => (
          <li key={c.label + i}>
            <i style={{ background: colors[i % colors.length] }} />
            <span className="cpr-cd-l">{c.label}</span>
            <span className="cpr-cd-v mono">{Math.round((Number(c.share) / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Section header row: the "NN" number on the left + the Open/Locked badge on the
// right, in a flex row so the badge can NEVER overlap the title below it (the old
// absolute badge crawled under the heading on the open sections).
function FreeSecHead({ num, role, t }) {
  return (
    <div className="cpr-fhead">
      {num ? <span className="cpr-eyebrow">{num}</span> : <span />}
      <FreeSectionBadge role={role} t={t} />
    </div>
  );
}

// Open content needs no "preview" badge (it just reads as intro/real content) —
// only the LOCKED sections get a badge, for contrast.
function FreeSectionBadge({ role, t }) {
  if (role === 'open' || role === 'peek') return null;
  return <span className="cpr-sbadge cpr-sbadge-lock"><Icon name="lock" size={9} /> {t('research.badgeLocked')}</span>;
}

// Sticky bottom unlock bar (persistent across the long scroll).
function UnlockBar({ count, t, onUpgrade }) {
  return (
    <div className="cpr-ubar">
      <div className="cpr-ubar-inner">
        <div className="cpr-ubar-txt">
          <span className="cpr-ubar-t"><Icon name="lock" size={12} /> {t('research.unlockBarTitle', { count })}</span>
          <span className="cpr-ubar-s">{t('research.unlockBarSub')}</span>
        </div>
        <button type="button" className="cpr-ubar-cta" onClick={onUpgrade}>
          <Icon name="sparkles" size={14} /> {t('research.upCta')}
        </button>
      </div>
    </div>
  );
}

/* The free report body — full 12-section spine, scroll-spy, unlock bar. */
function FreeReportView({ report, sources, domains, palette, t, onUpgrade, sectionTitle }) {
  const metrics = report.metrics || {};
  const [srcExpanded, setSrcExpanded] = useState(false);
  // Same body-backfill as the paid view (no-op on free: the market section is
  // locked, so there is no body to read — free keeps showing only the structured
  // aggregate the server already exposes).
  const market = useMemo(() => enrichMarket(metrics.market || null, report.sections), [metrics, report.sections]);
  const audience = metrics.audience || null;
  const archetypes = report.blocks?.archetypes || null;
  const channels = report.blocks?.channels || null;

  // The real free SECTION bodies the server sent (visibility=free → locked:false
  // → bodyMd present). For a lite onboarding report that's methodology + audience
  // — real analysis text, the genuine "this is real" proof. We render it crisp
  // rather than blurring it. Premium kinds arrive locked (no body) → blur shell.
  const secByKind = useMemo(() => {
    const m = new Map();
    (report.sections || []).forEach((s) => { if (s.kind) m.set(s.kind, s); });
    return m;
  }, [report.sections]);

  // Effective role per spine row, DATA-DRIVEN:
  //   open  — the section arrived free with real body text (or audience has a donut)
  //   peek  — locked, but a whitelisted chart aggregate (market/channels) exists
  //   locked— premium with nothing free to show → full-length secure blur
  const rows = useMemo(() => FREE_SPINE.map((s) => {
    if (s.id === 'exec') return { ...s, role: 'open', body: null };
    if (s.id === 'sources') return { ...s, role: 'sources', body: null };
    const sec = secByKind.get(s.id);
    const body = (sec && sec.locked === false && sec.bodyMd) ? sec.bodyMd : null;
    const hasAud = (audience && audience.segments && audience.segments.length >= 2) || (archetypes && archetypes.length);
    const hasMarket = market && (market.tam != null || market.sam != null || market.som != null);
    const hasChannels = channels && channels.length;
    let role;
    if (s.id === 'audience') role = (body || hasAud) ? 'open' : 'locked';
    else if (body) role = 'open';
    else if (s.id === 'market' && hasMarket) role = 'peek';
    else if (s.id === 'channels' && hasChannels) role = 'peek';
    else role = 'locked';
    return { ...s, role, body };
  }), [secByKind, market, channels, audience, archetypes]);

  const navIds = rows.map((r) => `cpr-${r.id}`);
  const activeNav = useScrollSpy(navIds);
  const openCount = rows.filter((r) => r.role === 'open' || r.role === 'peek').length;
  const lockedCount = rows.filter((r) => r.role === 'locked').length;

  const titleFor = (id) => {
    if (id === 'exec') return t('research.execTitle');
    if (id === 'sources') return t('research.verifiedReferences');
    // Prefer the localized canonical label so titles never leak English from
    // the worker's raw section.title; fall back to the server title if a kind
    // has no key.
    return t(`research.kind_${id}`, sectionTitle(id) || id);
  };

  return (
    <>
    <div className="cpr-grid cpr-grid-3 cpr-free">
      <nav className="cpr-snav cpr-snav-free" aria-label={t('research.navAria', 'Report sections')}>
        <div className="cpr-snav-head">
          {t('research.navOnPage')}
          <span className="cpr-snav-ct">{t('research.navOpenCount', { open: openCount, total: rows.length })}</span>
        </div>
        {rows.map((r) => {
          const id = `cpr-${r.id}`;
          const isOpen = r.role === 'open' || r.role === 'peek';
          return (
            <a
              key={id}
              href={`#${id}`}
              className={`cpr-snav-l${activeNav === id ? ' on' : ''}`}
              onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            >
              <span className="cpr-snav-ti"><Icon name={r.icon} size={14} /></span>
              <span className="cpr-snav-tl">{titleFor(r.id)}</span>
              {isOpen
                ? <span className="cpr-snav-od" aria-hidden="true" />
                : <span className="cpr-snav-lk" aria-hidden="true"><Icon name="lock" size={11} /></span>}
            </a>
          );
        })}
      </nav>

      <div className="cpr-col-main">
        {rows.map((r, i) => {
          const id = `cpr-${r.id}`;
          const num = String(i + 1).padStart(2, '0');
          const title = titleFor(r.id);

          // ---- OPEN: Executive summary (real headline stats + growth bars) ----
          if (r.id === 'exec') {
            const execSummary = plainSummary(report.summary);
            return (
              <section key={id} id={id} className="card cpr-section cpr-fsec cpr-fsec-open">
                <FreeSecHead num={num} role="open" t={t} />
                <h3 className="cpr-section-h">{title}</h3>
                <p className="cpr-fsec-lead">{t('research.execFreeLead')}</p>
                <ExecHighlights market={market} palette={palette} t={t} />
                {execSummary && <p className="cpr-exec-p">{execSummary}</p>}
              </section>
            );
          }

          // ---- OPEN: Audience (real analysis text + donut/archetypes when present) ----
          if (r.id === 'audience' && r.role === 'open') {
            return (
              <section key={id} id={id} className="card cpr-section cpr-fsec cpr-fsec-open">
                <FreeSecHead num={num} role="open" t={t} />
                <h3 className="cpr-section-h">{title}</h3>
                {audience && audience.segments && audience.segments.length >= 2 && (
                  <AudienceViz data={audience} colors={palette} t={t} />
                )}
                {archetypes && archetypes.length > 0 && <ArchetypeCards items={archetypes} t={t} />}
                {r.body && <div className="cpr-fsec-body"><ResearchMarkdown body={r.body} sourceIndex={EMPTY_SOURCE_INDEX} /></div>}
              </section>
            );
          }

          // ---- OPEN: a real free section body (e.g. Methodology) rendered crisp ----
          if (r.role === 'open') {
            return (
              <section key={id} id={id} className="card cpr-section cpr-fsec cpr-fsec-open">
                <FreeSecHead num={num} role="open" t={t} />
                <h3 className="cpr-section-h">{title}</h3>
                {r.body && <div className="cpr-fsec-body"><ResearchMarkdown body={r.body} sourceIndex={EMPTY_SOURCE_INDEX} /></div>}
              </section>
            );
          }

          // ---- PEEK: crisp whitelisted chart above a blurred fabricated body ----
          const isPeek = r.role === 'peek';
          const lockOne = t(`research.lock_${r.id}`, t('research.lockGeneric'));
          return (
            <section
              key={id}
              id={id}
              className={`card cpr-section cpr-fsec cpr-lsec${isPeek ? ' cpr-fsec-peek' : ''}`}
              aria-label={`${title} — ${t('research.lockedAria', 'locked, unlock with a paid plan')}`}
            >
              <FreeSecHead num={num} role="locked" t={t} />
              <h3 className="cpr-section-h">{title}</h3>

              {isPeek && r.id === 'market' && market && (
                <PeekChart t={t}>
                  <div className="cpr-viz cpr-viz-peek">
                    <div className="cpr-viz-head">
                      <span className="cpr-viz-t">{t('research.peekMarketTitle')}</span>
                    </div>
                    <BarGroup
                      rows={[['tam', market.tam], ['sam', market.sam], ['som', market.som]]
                        .filter(([, v]) => v != null)
                        .map(([k, v]) => ({ label: t(`research.metric_${k}`), value: v }))}
                      colors={palette}
                      unit={market.unit}
                    />
                    {market.series && market.series.length >= 2 && (
                      <div className="cpr-spark-wrap">
                        <span className="cpr-spark-cap">{t('research.chartGrowthCap')}</span>
                        <GrowthSpark series={market.series} color={palette[0]} unit={market.unit} />
                      </div>
                    )}
                  </div>
                </PeekChart>
              )}
              {isPeek && r.id === 'channels' && channels && channels.length > 0 && (
                <PeekChart t={t}>
                  <div className="cpr-viz cpr-viz-peek">
                    <div className="cpr-viz-head">
                      <span className="cpr-viz-t">{t('research.peekChannelsTitle')}</span>
                    </div>
                    <ChannelDonut items={channels} colors={palette} t={t} />
                  </div>
                </PeekChart>
              )}

              <div className="cpr-lbody">
                <LockSkeleton kind={r.id} />
                <div className="cpr-scrim" />
                <div className="cpr-lockfg">
                  <span className="cpr-lock-chip"><Icon name="lock" size={12} /> {title}</span>
                  <p className="cpr-lock-one">{lockOne}</p>
                  <button type="button" className="cpr-unlock-cta" onClick={onUpgrade}>
                    <Icon name="sparkles" size={13} /> {t('research.upCta')}
                  </button>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <div className="cpr-col-side">
        <section className="card cpr-up">
          <span className="cpr-eyebrow">{t('research.upEyebrow')}</span>
          <h3>{t('research.upTitle')}</h3>
          <p>{t('research.upBody')}</p>
          <ul>
            <li><Icon name="check" size={12} /> {t('research.upB1')}</li>
            <li><Icon name="check" size={12} /> {t('research.upB2')}</li>
            <li><Icon name="check" size={12} /> {t('research.upB3')}</li>
            <li><Icon name="check" size={12} /> {t('research.upB4')}</li>
          </ul>
          <button className="btn btn-primary cpr-up-cta" type="button" onClick={onUpgrade}>
            {t('research.upCta')} <Icon name="arrowRight" size={13} />
          </button>
          <span className="cpr-up-sub">{t('research.upSub')}</span>
        </section>

        {/* Sources on the RIGHT for free too — consistent with the entitled
            layout. Free shows bare domain chips only (no titles/urls — the
            server field-picks {id,domain} for non-entitled callers) + an
            unlock CTA; the full referenced list is paywalled. */}
        {domains.length > 0 && (
          <section className="card cpr-sources">
            <div className="cpr-src-head">
              <span className="cpr-eyebrow">{t('research.sourcesTitle')}</span>
              <span className="cpr-src-n">{sources.length}</span>
            </div>
            <div className="cpr-src-sub">{t('research.verifiedReferences')}</div>
            {/* Free now gets the same VISIBLE citation fields as paid (title, url,
                verified) so the panel looks identical — the paywall is depth
                (more sources + deeper analysis), surfaced by the note below. */}
            <div className="cpr-src-list">
              {sources.slice(0, srcExpanded ? sources.length : 12).map((source) => {
                const Row = source.url ? 'a' : 'div';
                const extra = source.url ? { href: source.url, target: '_blank', rel: 'noreferrer' } : {};
                return (
                  <Row key={source.id} className="cpr-src-row" {...extra}>
                    <span className="cpr-src-fav" style={{ background: domainColor(source.domain || '') }}>{(source.domain || '?')[0].toUpperCase()}</span>
                    <div className="cpr-src-main">
                      <div className="cpr-src-t">{source.title || source.domain}</div>
                      <div className="cpr-src-m mono">{source.domain}{source.verified && <> · <Icon name="check" size={9} /></>}</div>
                    </div>
                  </Row>
                );
              })}
            </div>
            {sources.length > 12 && (
              <button type="button" className="cpr-src-more" onClick={() => setSrcExpanded((v) => !v)}>
                {srcExpanded ? t('research.sourcesShowLess') : t('research.sourcesShowMore', { count: sources.length - 12 })}
              </button>
            )}
            <button type="button" className="cpr-src-unlock" onClick={onUpgrade}>
              <Icon name="lock" size={11} /> {t('research.sourcesLockedHint')}
            </button>
            <span className="cpr-src-note"><Icon name="check" size={11} /> {t('research.sourcesNote')}</span>
          </section>
        )}
      </div>
    </div>
    </>
  );
}

/* ---------- report (teaser + full) ---------- */

function ReportView({ report, reports = [], selectedReportId, onSelectReport, lang, onRefresh, refreshing, creationBlocked, noBrand, onUpgrade, error, brandLogoUrl, brandName = '', brandColors = [] }) {
  const { t, i18n } = useTranslation();
  // The report is a document in ONE language (report.language). Render its
  // CONTENT chrome (section titles, leads, glossary, nav, stats, lock teasers)
  // in THAT language via `rt`, so the report reads consistently even when the UI
  // is toggled to a different language. The app chrome AROUND the report
  // (Refresh / Export PDF / version pills / error banner) keeps the UI `t`.
  const rt = useMemo(() => i18n.getFixedT(report.language || lang || 'en'), [i18n, report.language, lang]);
  const [srcExpanded, setSrcExpanded] = useState(false);
  const readyReports = (reports || []).filter((r) => r.status === 'ready');
  const sources = report.sources || [];
  const sections = report.sections || [];
  const entitled = report.entitled === true;
  const sourceIndex = useMemo(() => {
    const byId = new Map();
    const numberOf = new Map();
    sources.forEach((source, index) => {
      byId.set(source.id, source);
      numberOf.set(source.id, index + 1);
    });
    return { byId, numberOf };
  }, [sources]);
  const domains = [...new Set(sources.map((s) => s.domain).filter(Boolean))];
  const openSections = sections.filter((s) => !s.locked);
  const lockedSections = sections.filter((s) => s.locked);
  // kind → real section title (from open or locked shells the server sends);
  // the free spine falls back to the localized kind label when absent.
  const titleByKind = useMemo(() => {
    const m = new Map();
    sections.forEach((s) => { if (s.kind && s.title) m.set(s.kind, s.title); });
    return m;
  }, [sections]);
  // Left scroll-spy nav: exec summary + each open section.
  const navItems = [
    ...(report.summary ? [{ id: 'cpr-exec', label: rt('research.execTitle'), icon: 'book' }] : []),
    // Clean canonical title + spine icon (not the raw, often-very-long section
    // title) so the entitled menu reads like the free one — in the report language.
    ...openSections.map((s) => ({ id: `cpr-${s.kind}`, label: rt(`research.kind_${s.kind}`, s.title), icon: SPINE_ICON[s.kind] || 'book' })),
  ];
  const activeNav = useScrollSpy(navItems.map((n) => n.id));
  const palette = useMemo(() => chartColors(brandColors), [brandColors]);
  const metrics = report.metrics || null;
  const blocks = report.blocks || null;
  // The structured market metrics are best-effort (sam/som often null); backfill
  // them from the body's TAM/SAM/SOM table so exec stats + the funnel show the
  // full picture the report actually contains. No-op when already complete.
  const market = useMemo(() => enrichMarket(metrics?.market, sections), [metrics, sections]);

  return (
    <div className="cpr-report">
      <div className="cpr-rep-head">
        <div className="cpr-rep-head-l">
          {(brandLogoUrl || brandName) && <BrandTile logoUrl={brandLogoUrl} name={brandName} size="lg" />}
          <div className="cpr-rep-head-t">
            <h2 className="cpr-rep-title">
              {brandName
                ? <>{t('research.reportTitleFor')} <span className="cpr-bid-ac">{brandName}</span></>
                : t('research.reportTitle')}
            </h2>
            <div className="cpr-rep-meta">
              <span className="pill pill-success"><span className="dot" /> {t('research.statusReady')}</span>
              <span className="pill">v{report.version}</span>
              {brandColors.length > 0 && (
                <span className="cpr-bid-sw">{brandColors.map((c, i) => <i key={i} style={{ background: c }} />)}</span>
              )}
              <span className="cpr-rep-date mono">{t('research.generatedOn', { date: formatDate(report.completedAt || report.createdAt, lang) })}</span>
            </div>
          </div>
        </div>
        <div className="cpr-rep-actions">
          {readyReports.length > 1 && (
            <select
              className="cpr-ver-sel"
              value={selectedReportId || report.id}
              onChange={(e) => onSelectReport?.(e.target.value)}
              title={t('research.versionPick')}
            >
              {readyReports.map((r) => (
                <option key={r.id} value={r.id}>
                  {t('research.versionOption', { version: r.version, date: formatDate(r.completedAt || r.createdAt, lang) })}
                </option>
              ))}
            </select>
          )}
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => (entitled ? window.open(`/app/research/print/${report.id}`, '_blank', 'noopener') : onUpgrade?.())}
            title={entitled ? undefined : t('research.exportPdfLocked')}
          >
            <Icon name={entitled ? 'download' : 'lock'} size={13} /> {t('research.exportPdf')}
          </button>{/* free: shows as a locked CTA → upgrade (data is already server-side entitlement-gated) */}
          <button className="btn btn-secondary btn-sm" type="button" disabled={refreshing || noBrand || creationBlocked} onClick={entitled ? onRefresh : onUpgrade} aria-describedby={noBrand ? 'no-brand-guard-hint-research' : undefined} title={noBrand ? t('noBrandGuard.disabledHint') : entitled ? undefined : t('research.refreshLockedFree')}>
            <Icon name={entitled ? 'refresh' : 'lock'} size={13} /> {refreshing ? t('research.starting') : t('research.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="cpr-quota-banner" role="alert">
          <Icon name="lock" size={14} />
          <span>{error}</span>
          <button type="button" className="cpr-unlock-cta" onClick={onUpgrade}>
            <Icon name="sparkles" size={13} /> {t('research.upCta')}
          </button>
        </div>
      )}

      {!entitled ? (
        <FreeReportView
          report={report}
          sources={sources}
          domains={domains}
          palette={palette}
          t={rt}
          onUpgrade={onUpgrade}
          sectionTitle={(kind) => titleByKind.get(kind) || null}
        />
      ) : (
      <div className="cpr-grid cpr-grid-3">
        {navItems.length > 1 && (
          <nav className="cpr-snav cpr-snav-free" aria-label={t('research.navAria', 'Report sections')}>
            {navItems.map((it) => (
              <a
                key={it.id}
                href={`#${it.id}`}
                className={`cpr-snav-l${activeNav === it.id ? ' on' : ''}`}
                onClick={(e) => { e.preventDefault(); document.getElementById(it.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              >
                <span className="cpr-snav-ti"><Icon name={it.icon} size={14} /></span>
                <span className="cpr-snav-tl">{it.label}</span>
              </a>
            ))}
          </nav>
        )}
        <div className="cpr-col-main">
          {plainSummary(report.summary) && (
            <section className="card cpr-section" id="cpr-exec">
              <span className="cpr-eyebrow">{rt('research.execEyebrow')}</span>
              <h3 className="cpr-section-h">{rt('research.execTitle')}</h3>
              <ExecHighlights market={market} palette={palette} t={rt} />
              <p className="cpr-exec-p">{plainSummary(report.summary)}</p>
            </section>
          )}

          {openSections.map((section, i) => (
            <section key={section.id} className="card cpr-section" id={`cpr-${section.kind}`}>
              <span className="cpr-eyebrow">{rt('research.sectionN', { n: String(i + 1).padStart(2, '0') })}</span>
              <h3 className="cpr-section-h">{rt(`research.kind_${section.kind}`, section.title)}</h3>
              {section.kind === 'market' && market && <MarketViz data={market} colors={palette} t={rt} />}
              {section.kind === 'audience' && metrics?.audience && <AudienceViz data={metrics.audience} colors={palette} t={rt} />}
              {section.kind === 'audience' && blocks?.archetypes && <ArchetypeCards items={blocks.archetypes} t={rt} />}
              {section.kind === 'competitors' && blocks?.competitors && <CompetitorCards items={blocks.competitors} t={rt} />}
              {section.kind === 'channels' && blocks?.channels && <ChannelBars items={blocks.channels} colors={palette} t={rt} />}
              {section.kind === 'content_angles' && blocks?.angles && <AnglePillars items={blocks.angles} colors={palette} t={rt} />}
              {section.kind === 'action_plan' && blocks?.roadmap && <Roadmap phases={blocks.roadmap.phases} kpis={blocks.roadmap.kpis} t={rt} />}
              {section.kind === 'economics' && blocks?.economics && <EconomicsTable data={blocks.economics} t={rt} />}
              {section.kind === 'positioning' && blocks?.positioning && <PositioningBlock data={blocks.positioning} t={rt} />}
              <ResearchMarkdown
                body={section.bodyMd}
                sourceIndex={sourceIndex}
                chartTables
                skipSeries={section.kind === 'market' ? market?.series : null}
                chartPalette={palette}
              />
            </section>
          ))}

          {lockedSections.map((section) => (
            section.kind === 'audience' ? (
              // Free hook: the audience section ALWAYS stays an open preview (never
              // the full blur) — the real donut + archetypes show when present, and
              // each self-hides when its data is absent, so audience never collapses
              // into a LockedSectionBlur (the regression a free user hit when the
              // donut metrics were missing on a non-lite report).
              <section key={section.id} className="card cpr-section cpr-lock-preview" id={`cpr-${section.kind}`}>
                <span className="cpr-eyebrow">{rt('research.kind_audience', section.title)}</span>
                <h3 className="cpr-section-h">{rt('research.kind_audience', section.title)}</h3>
                {metrics?.audience && <AudienceViz data={metrics.audience} colors={palette} t={rt} />}
                {blocks?.archetypes && <ArchetypeCards items={blocks.archetypes} t={rt} />}
                <div className="cpr-lock-more"><Icon name="lock" size={12} /> {rt(`research.lock_${section.kind}`, rt('research.lockGeneric'))}</div>
              </section>
            ) : (
              <LockedSectionBlur key={section.id} section={section} t={rt} onUpgrade={onUpgrade} />
            )
          ))}
        </div>

        <div className="cpr-col-side">
          {!entitled && (
            <section className="card cpr-up">
              <span className="cpr-eyebrow">{t('research.upEyebrow')}</span>
              <h3>{t('research.upTitle')}</h3>
              <p>{t('research.upBody')}</p>
              <ul>
                <li><Icon name="check" size={12} /> {t('research.upB1')}</li>
                <li><Icon name="check" size={12} /> {t('research.upB2')}</li>
                <li><Icon name="check" size={12} /> {t('research.upB3')}</li>
                <li><Icon name="check" size={12} /> {t('research.upB4')}</li>
              </ul>
              <button className="btn btn-primary cpr-up-cta" type="button" onClick={onUpgrade}>
                {t('research.upCta')} <Icon name="arrowRight" size={13} />
              </button>
              <span className="cpr-up-sub">{t('research.upSub')}</span>
            </section>
          )}

          <section className="card cpr-sources">
            <div className="cpr-src-head">
              <span className="cpr-eyebrow">{t('research.sourcesTitle')}</span>
              <span className="cpr-src-n">{sources.length}</span>
            </div>
            <div className="cpr-src-sub">{entitled ? t('research.verifiedReferences') : t('research.sourcesMeta', { domains: domains.length })}</div>
            {entitled ? (
              <>
                <div className="cpr-src-list">
                  {sources.slice(0, srcExpanded ? sources.length : 12).map((source) => {
                    const Row = source.url ? 'a' : 'div';
                    const extra = source.url ? { href: source.url, target: '_blank', rel: 'noreferrer' } : {};
                    return (
                      <Row key={source.id} className="cpr-src-row" {...extra}>
                        <span className="cpr-src-fav" style={{ background: domainColor(source.domain || '') }}>{(source.domain || '?')[0].toUpperCase()}</span>
                        <div className="cpr-src-main">
                          <div className="cpr-src-t">{source.title || source.domain}</div>
                          <div className="cpr-src-m mono">{source.domain}{source.verified && <> · <Icon name="check" size={9} /></>}</div>
                        </div>
                      </Row>
                    );
                  })}
                </div>
                {sources.length > 12 && (
                  <button type="button" className="cpr-src-more" onClick={() => setSrcExpanded((v) => !v)}>
                    {srcExpanded ? t('research.sourcesShowLess') : t('research.sourcesShowMore', { count: sources.length - 12 })}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="cpr-doms">
                  {domains.slice(0, 16).map((domain) => (
                    <span key={domain} className="cpr-dch"><i style={{ background: domainColor(domain) }}>{domain[0].toUpperCase()}</i>{domain}</span>
                  ))}
                </div>
                <button type="button" className="cpr-src-unlock" onClick={onUpgrade}>
                  <Icon name="lock" size={11} /> {t('research.sourcesLockedHint')}
                </button>
              </>
            )}
            <span className="cpr-src-note"><Icon name="check" size={11} /> {t('research.sourcesNote')}</span>
          </section>
        </div>
      </div>
      )}

      {report.doneWith?.name && (
        <footer className="cpr-foot">
          {t('research.doneWith')}{' '}
          {report.doneWith.url ? (
            <a href={report.doneWith.url} target="_blank" rel="noreferrer">{report.doneWith.name}</a>
          ) : (
            <b>{report.doneWith.name}</b>
          )}
        </footer>
      )}
    </div>
  );
}
